import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import {
  OrderItemStatus,
  OrderStatus,
  type OrderEventPayload,
  type OrderItemEventPayload,
  type TakeawayStatus,
} from '@/lib/shared-types';
import { baseApi } from '@/Redux/baseApi';
import type { RootState } from '@/Redux/store';

/**
 * The Operations reads — orders, the takeaway list, kitchen stations and the two
 * kitchen boards — on the cache layer instead of `useApi`.
 *
 * Why these five and not the other thirty-seven: they are the reads an operator
 * leaves and comes back to all shift. `useApi` starts from nothing on every
 * mount, so walking Orders → Kitchen → Orders costs three full round trips and
 * three blank screens. Here the second visit paints from cache and revalidates
 * behind it.
 *
 * **Reads only.** `baseApi` says in its own header that it is deliberately not a
 * write path for orders — an RTK Query mutation lives in memory and a closed tab
 * loses it, while a durable staff write belongs to `@/lib/offline` with its
 * `operationId`. So the screens keep firing their writes through the axios
 * client exactly as before, and tell the cache what changed by dispatching
 * {@link invalidateOrder} / {@link invalidateBoards} / {@link invalidateOrderLists}
 * afterwards. The effect asked for is the same — one status change repaints every
 * screen showing that order, not just the one that fired it — without making the
 * cache own money-moving writes it cannot persist.
 *
 * ## Freshness, and why the socket does not invalidate
 *
 * A cook must see a change within a second, and an invalidation is a round trip.
 * So the two paths are split by what the payload can answer:
 *
 * | trigger | mechanism | cost |
 * |---|---|---|
 * | `ITEM_*`, `ORDER_*`, `TAKEAWAY_*` status events | {@link applyItemEvent} / {@link applyOrderEvent} — a targeted `updateQueryData` patch of every cached entry, from the payload | 0 requests |
 * | events that introduce a row nothing has yet (`ORDER_CREATED`, `ORDER_SENT_TO_KITCHEN`, `TAKEAWAY_RELEASED`) | tag invalidation | 1 request — unavoidable, the payload carries no ticket body |
 * | the operator's own click | tag invalidation | 1 request, same as the old `reload()` |
 *
 * The patch walks *every* cached entry of an endpoint, not just the arguments
 * the mounted screen happens to be using, so a filter combination sitting
 * unmounted in the cache cannot come back wrong when it is next opened.
 */

// ---------------------------------------------------------------- row shapes

export interface OrderItemRow {
  id: string;
  name: string;
  quantity: string;
  unitPrice: string;
  /** Fixed-scale decimal string. Never `Number()` it — format with `useCurrency()`. */
  total: string;
  status: string;
  notes: string | null;
  preparingAt: string | null;
  readyAt: string | null;
  /** §32 — the same pickup stamp `TicketItem` carries. `stampItem` writes it and
   *  `GET /orders` answers it; this type was the only one of the three silent
   *  about it, which made the pass unable to read what it was already given. */
  pickedUpAt: string | null;
  servedAt: string | null;
  modifiers?: { id?: string; name: string; quantity: number; price: string }[];
}

export interface OrderRow {
  id: string;
  number: string;
  status: OrderStatus;
  fulfillmentType: string;
  takeawayStatus: TakeawayStatus | null;
  releaseStrategy: string | null;
  scheduledFor: string | null;
  customerName: string | null;
  customerPhone: string | null;
  notes: string | null;
  subtotal: string;
  total: string;
  createdAt: string;
  branchId: string;
  session?: { id: string; tableId: string | null } | null;
  items: OrderItemRow[];
  /**
   * A guest's QR order that has stopped at CONFIRMED and is waiting for a waiter
   * to read it back at the table before it is sent.
   *
   * Computed and read-only, and the pair has to stay that way. The venue decides
   * whether the stop exists at all (`ordering.requireWaiterConfirmation`) and how
   * long a wait is allowed to run (`ordering.confirmationSlaSeconds`), so a
   * client that derived either from `status` and a hardcoded number would go on
   * reddening tickets in a venue that turned the whole feature off, and would
   * disagree with the server the day the SLA is changed from a settings screen.
   * Both are absent on every other order, which is why they are optional.
   */
  awaitingWaiterConfirmation?: boolean;
  /**
   * The instant this wait becomes late, stamped by the server. Compared against
   * the clock and never recomputed: `confirmedAt + slaSeconds` done here would be
   * a second copy of a rule that lives in one place.
   */
  confirmationDueAt?: string | null;
}

export interface Station {
  id: string;
  name: string;
  code: string;
  slaSeconds: number | null;
}

export interface TicketItem {
  id: string;
  orderId: string;
  name: string;
  quantity: string;
  status: string;
  notes: string | null;
  preparingAt: string | null;
  readyAt: string | null;
  /** §32 — the pickup-delay and order-to-table stamps. */
  pickedUpAt: string | null;
  servedAt: string | null;
  modifiers: { name: string; quantity: number; price: string }[];
}

export interface Ticket {
  id: string;
  number: string;
  status: string;
  priority: number;
  createdAt: string;
  startedAt: string | null;
  readyAt: string | null;
  fulfillmentType: string;
  orderId: string;
  orderNumber: string;
  stationId: string;
  stationName: string;
  notes: string | null;
  slaSeconds: number | null;
  elapsedSeconds: number;
  isSlaBreached: boolean;
  items: TicketItem[];
  /** Takeaway board only. */
  takeawayStatus?: string | null;
  customerName?: string | null;
  scheduledFor?: string | null;
}

/**
 * S-32 — a sent line that reached no kitchen.
 *
 * `GET /kitchen/unrouted` is branch-level, not per-station, because a line with
 * no station is on no station's board: routing comes from the PRODUCT's
 * `stationId` and the send path drops whatever has none. The row carries both
 * halves of the fix — the line, and the catalog product behind it — so the
 * screen can offer "this order" and "always" without a second read.
 */
export interface UnroutedLine {
  id: string;
  orderId: string;
  orderNumber: string;
  orderStatus: string;
  branchId: string;
  branchName: string;
  fulfillmentType: string;
  tableId: string | null;
  customerName: string | null;
  name: string;
  quantity: string;
  status: string;
  notes: string | null;
  sentAt: string;
  waitingSeconds: number;
  modifiers: { name: string; quantity: number; price: string }[];
  /** The CATALOG product — what "always route this" would be editing. */
  itemId: string;
  catalogName: string;
  catalogType: string;
  /** S-09 — false for a product that is sold rather than prepared (PS5 hours). */
  canRouteAlways: boolean;
}

export interface UnroutedQuery {
  branchId?: string;
  page?: number;
  limit?: number;
}

/** A list read, already out of the §3 `{ data, meta }` envelope. */
export interface Page<T> {
  rows: T[];
  total: number;
}

// --------------------------------------------------------------- query args
//
// One interface per DTO, and nothing that is not on the DTO: the global
// `ValidationPipe` runs `forbidNonWhitelisted`, so a single stray query param is
// a 400 rather than an ignored filter. Each of these was read off the DTO class
// it mirrors.

/** `OrderQueryDto` (+ `PaginationDto`). `from`/`to` are the I-09 `createdAt` window. */
export interface OrderListQuery {
  status?: string;
  fulfillmentType?: string;
  takeawayStatus?: string;
  branchId?: string;
  tableId?: string;
  sessionId?: string;
  checkId?: string;
  search?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
  /**
   * Only the orders a waiter still has to read back and send, longest wait
   * first. The ordering is the server's and is not re-derived here — see
   * `AwaitingConfirmationLane`.
   */
  awaitingConfirmation?: boolean;
}

/**
 * `TakeawayQueryDto` (+ `PaginationDto`) — note what is *absent*: it declares no
 * `from`/`to` and no `fulfillmentType`. The screen used to spread the same day
 * bounds into both tabs, which made every dated takeaway search a 400.
 */
export interface TakeawayListQuery {
  status?: string;
  branchId?: string;
  releaseStrategy?: string;
  search?: string;
  page?: number;
  limit?: number;
}

/** `KitchenBoardQueryDto` with no `stationId` — the station picker. */
export interface StationListQuery {
  branchId?: string;
  includeInactive?: boolean;
  page?: number;
  limit?: number;
}

/** `KitchenBoardQueryDto` with a `stationId` — that station's board. */
export interface KitchenBoardQuery {
  stationId: string;
  /** One status or a comma-separated list; the DTO splits it. */
  status?: string;
  fulfillmentType?: string;
  branchId?: string;
  page?: number;
  limit?: number;
}

/** `TakeawayQueueQueryDto` — no `fulfillmentType`, the route is already takeaway. */
export interface TakeawayQueueQuery {
  stationId?: string;
  status?: string;
  branchId?: string;
  page?: number;
  limit?: number;
}

// ------------------------------------------------------------------- helpers

/** Drops empties so a cleared filter never reaches the API as `?status=`. */
function clean<T extends object>(args: T): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(args as Record<string, unknown>)) {
    if (value === '' || value === null || value === undefined) continue;
    out[key] = value;
  }
  return out;
}

/**
 * The cache key ignores the difference between an absent filter and a blank one,
 * so clearing a `<Select>` back to "Any" reuses the entry it started from
 * instead of opening a second one that has to be fetched from scratch.
 */
function stableKey({ endpointName, queryArgs }: { endpointName: string; queryArgs: unknown }) {
  const args = clean((queryArgs ?? {}) as object);
  return `${endpointName}(${JSON.stringify(args, Object.keys(args).sort())})`;
}

/** §3 envelope → rows + total. Kitchen answers a bare array; orders answer a page. */
function toPage<T>(payload: unknown): Page<T> {
  if (Array.isArray(payload)) return { rows: payload as T[], total: payload.length };
  const body = payload as { data?: T[]; meta?: { total?: number } } | null;
  const rows = body?.data ?? [];
  return { rows, total: body?.meta?.total ?? rows.length };
}

// ---------------------------------------------------------------------- tags
//
// `Order`/`Station` are declared in `baseApi`'s `API_TAGS`; these are the ids
// this slice agrees to spell one way.

/** Every order list. Invalidated when an order appears or leaves a list. */
const ORDER_LIST = { type: 'Order', id: 'LIST' } as const;
/**
 * Both kitchen boards. Deliberately not `Order/LIST`: a draft order being
 * created moves the orders screen and no board, and a KDS terminal should not
 * spend a request on it.
 */
const BOARD = { type: 'Station', id: 'BOARD' } as const;
/** The station picker. */
const STATION_LIST = { type: 'Station', id: 'LIST' } as const;

const orderTag = (id: string) => ({ type: 'Order', id }) as const;

/** Rows → per-order tags, so one order's change reaches every list carrying it. */
function orderTags(rows: { id: string }[]) {
  return [ORDER_LIST, ...rows.map((row) => orderTag(row.id))];
}

function ticketTags(rows: Ticket[], stationId?: string) {
  return [
    BOARD,
    ...(stationId ? [{ type: 'Station', id: stationId } as const] : []),
    ...rows.map((ticket) => orderTag(ticket.orderId)),
  ];
}

// ----------------------------------------------------------------- endpoints

export const operationsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /** `GET /orders` — `orders.view`. */
    listOrders: build.query<Page<OrderRow>, OrderListQuery>({
      query: (args) => ({ url: '/orders', params: clean(args) }),
      serializeQueryArgs: stableKey,
      transformResponse: toPage<OrderRow>,
      providesTags: (result) => orderTags(result?.rows ?? []),
    }),

    /** `GET /takeaway` — `orders.view`. The §8.2 release lane over the same orders. */
    listTakeawayOrders: build.query<Page<OrderRow>, TakeawayListQuery>({
      query: (args) => ({ url: '/takeaway', params: clean(args) }),
      serializeQueryArgs: stableKey,
      transformResponse: toPage<OrderRow>,
      providesTags: (result) => orderTags(result?.rows ?? []),
    }),

    /** `GET /kitchen` with no `stationId` — `kitchen.view`. The station picker. */
    listStations: build.query<Page<Station>, StationListQuery>({
      query: (args) => ({ url: '/kitchen', params: clean(args) }),
      serializeQueryArgs: stableKey,
      transformResponse: toPage<Station>,
      providesTags: (result) => [
        STATION_LIST,
        ...(result?.rows ?? []).map((station) => ({ type: 'Station', id: station.id }) as const),
      ],
    }),

    /**
     * `GET /kitchen?stationId=` — `kitchen.view`. One station's dine-in board.
     *
     * `keepUnusedDataFor` is cut to 30s against the 300s default: cached tickets
     * are worth painting instantly on the way back to the board, but a board
     * that has been closed for five minutes is a board nobody should be asked
     * to trust, even for the second it takes to revalidate.
     */
    kitchenBoard: build.query<Page<Ticket>, KitchenBoardQuery>({
      query: (args) => ({ url: '/kitchen', params: clean(args) }),
      serializeQueryArgs: stableKey,
      transformResponse: toPage<Ticket>,
      keepUnusedDataFor: 30,
      providesTags: (result, _error, args) => ticketTags(result?.rows ?? [], args.stationId),
    }),

    /**
     * `GET /kitchen/unrouted` — `kitchen.view`. The lines that reached nobody.
     *
     * Tagged `BOARD` like the two ticket boards: routing one of these lines
     * creates a ticket, so the lane it leaves and the board it arrives on have
     * to refetch together or the cook watches it vanish into nothing.
     */
    kitchenUnrouted: build.query<Page<UnroutedLine>, UnroutedQuery>({
      query: (args) => ({ url: '/kitchen/unrouted', params: clean(args) }),
      serializeQueryArgs: stableKey,
      transformResponse: toPage<UnroutedLine>,
      keepUnusedDataFor: 30,
      providesTags: (result) => [
        BOARD,
        ...(result?.rows ?? []).map((line) => orderTag(line.orderId)),
      ],
    }),

    /** `GET /kitchen/takeaway-queue` — `kitchen.view`. §8.1's separate board. */
    kitchenTakeawayQueue: build.query<Page<Ticket>, TakeawayQueueQuery>({
      query: (args) => ({ url: '/kitchen/takeaway-queue', params: clean(args) }),
      serializeQueryArgs: stableKey,
      transformResponse: toPage<Ticket>,
      keepUnusedDataFor: 30,
      providesTags: (result, _error, args) => ticketTags(result?.rows ?? [], args.stationId),
    }),
  }),
});

export const {
  useListOrdersQuery,
  useListTakeawayOrdersQuery,
  useListStationsQuery,
  useKitchenBoardQuery,
  useKitchenTakeawayQueueQuery,
  useKitchenUnroutedQuery,
} = operationsApi;

// -------------------------------------------------------------- invalidation
//
// What a screen dispatches after one of its own writes lands. The tag does the
// fan-out: an order the kitchen board and the orders list are both showing is
// refetched by both, including the screen that is merely mounted elsewhere in
// the router.

/** One order changed — every list and board carrying it refetches. */
export const invalidateOrder = (orderId: string) =>
  operationsApi.util.invalidateTags([orderTag(orderId)]);

/** Tickets were created, re-sorted or recalled — both boards refetch. */
export const invalidateBoards = () => operationsApi.util.invalidateTags([BOARD]);

/** An order joined or left a list (created, cancelled, voided, released). */
export const invalidateOrderLists = () => operationsApi.util.invalidateTags([ORDER_LIST]);

/**
 * Someone edited the kitchen — the station picker refetches.
 *
 * `STATION_LIST` and `BOARD` are both `Station` tags with different ids, so
 * `invalidateBoards()` never reached the picker: `Kitchen.tsx` held the list a
 * station had just been added to, or deleted from, for its whole 300s window.
 * Stations are written through axios in `StationsTab`, so this is the call that
 * has to be made by hand.
 */
export const invalidateStations = () => operationsApi.util.invalidateTags([STATION_LIST]);

// ------------------------------------------------------------------ realtime

type OperationsThunk = ThunkAction<void, RootState, undefined, UnknownAction>;

type CachedQuery = { endpointName?: string; originalArgs?: unknown };

/** Every set of arguments this endpoint currently holds an entry for. */
function cachedArgs<T>(state: RootState, endpointName: string): T[] {
  const slice = (state as unknown as Record<string, { queries?: Record<string, CachedQuery> }>)[
    baseApi.reducerPath
  ];
  const queries = slice?.queries;
  if (!queries) return [];
  const out: T[] = [];
  for (const entry of Object.values(queries)) {
    if (entry && entry.endpointName === endpointName) out.push(entry.originalArgs as T);
  }
  return out;
}

/**
 * §15.2 stamps the item lands on. The event carries `occurredAt`, so the board
 * can show the pickup and serve times `KitchenItemActions` prints without
 * waiting for a refetch to hand them over.
 */
interface StampableItem {
  status: string;
  preparingAt?: string | null;
  readyAt?: string | null;
  pickedUpAt?: string | null;
  servedAt?: string | null;
}

function stampItem(item: StampableItem, status: string, at: string): void {
  item.status = status;
  if (status === OrderItemStatus.PREPARING) item.preparingAt ??= at;
  if (status === OrderItemStatus.READY) item.readyAt ??= at;
  if (status === OrderItemStatus.PICKED_UP) item.pickedUpAt ??= at;
  if (status === OrderItemStatus.SERVED) item.servedAt ??= at;
}

/**
 * An item moved somewhere in the venue — patch it into every cached list and
 * board that holds it, from the payload alone.
 *
 * No invalidation: this is the event a cook is waiting on, and a round trip is
 * exactly what the constraint forbids here. Ticket-level status is left to the
 * service — deriving "all items ready ⇒ ticket READY" on the client would be a
 * second copy of a rule that lives in `kitchen.service`, and a wrong badge is
 * worse than a badge that lags its own items by one refetch.
 */
export const applyItemEvent =
  (payload: OrderItemEventPayload): OperationsThunk =>
  (dispatch, getState) => {
    const state = getState();
    const { orderId, orderItemId, status, occurredAt } = payload;

    const patchTickets = (draft: Page<Ticket>) => {
      for (const ticket of draft.rows) {
        for (const item of ticket.items) {
          if (item.id === orderItemId) stampItem(item, status, occurredAt);
        }
      }
    };

    const patchOrders = (draft: Page<OrderRow>) => {
      for (const order of draft.rows) {
        if (order.id !== orderId) continue;
        for (const item of order.items ?? []) {
          if (item.id === orderItemId) stampItem(item, status, occurredAt);
        }
      }
    };

    for (const args of cachedArgs<KitchenBoardQuery>(state, 'kitchenBoard')) {
      dispatch(operationsApi.util.updateQueryData('kitchenBoard', args, patchTickets));
    }
    for (const args of cachedArgs<TakeawayQueueQuery>(state, 'kitchenTakeawayQueue')) {
      dispatch(operationsApi.util.updateQueryData('kitchenTakeawayQueue', args, patchTickets));
    }
    for (const args of cachedArgs<OrderListQuery>(state, 'listOrders')) {
      dispatch(operationsApi.util.updateQueryData('listOrders', args, patchOrders));
    }
    for (const args of cachedArgs<TakeawayListQuery>(state, 'listTakeawayOrders')) {
      dispatch(operationsApi.util.updateQueryData('listTakeawayOrders', args, patchOrders));
    }
  };

/**
 * An order's own lifecycle moved. `field` says which of the two statuses the
 * event is about — `ORDER_*` events carry the order status, `TAKEAWAY_*` events
 * carry the §8.2 one, and both lanes render from the same rows.
 */
export const applyOrderEvent =
  (payload: OrderEventPayload, field: 'status' | 'takeawayStatus' = 'status'): OperationsThunk =>
  (dispatch, getState) => {
    const state = getState();

    const patch = (draft: Page<OrderRow>) => {
      for (const order of draft.rows) {
        if (order.id !== payload.orderId) continue;
        if (field === 'takeawayStatus') order.takeawayStatus = payload.status as TakeawayStatus;
        else {
          order.status = payload.status as OrderStatus;
          /*
           * The waiter's stop is over the moment the order leaves CONFIRMED, and
           * the awaiting lane has to lose the row in that same moment.
           *
           * `awaitingWaiterConfirmation` is a snapshot of the instant the row was
           * read; nothing about a status patch refreshes it. Leave it alone and
           * the lane keeps drawing an order the kitchen already has — with its
           * clock still running and its Send button still offered — for as long
           * as the refetch behind the event takes to land. Clearing is the one
           * half of this pair the client may write, because it is the server's
           * own rule read backwards: no order outside CONFIRMED is waiting for
           * anybody. Nothing here ever sets it to `true` — whether the stop
           * exists at all is the venue's setting to answer, not ours.
           */
          if (payload.status !== OrderStatus.CONFIRMED) {
            order.awaitingWaiterConfirmation = false;
            order.confirmationDueAt = null;
          }
        }
      }
    };

    for (const args of cachedArgs<OrderListQuery>(state, 'listOrders')) {
      dispatch(operationsApi.util.updateQueryData('listOrders', args, patch));
    }
    for (const args of cachedArgs<TakeawayListQuery>(state, 'listTakeawayOrders')) {
      dispatch(operationsApi.util.updateQueryData('listTakeawayOrders', args, patch));
    }
    // The board prints the takeaway status too, and it is one field.
    if (field === 'takeawayStatus') {
      const patchTickets = (draft: Page<Ticket>) => {
        for (const ticket of draft.rows) {
          if (ticket.orderId === payload.orderId) ticket.takeawayStatus = payload.status;
        }
      };
      for (const args of cachedArgs<TakeawayQueueQuery>(state, 'kitchenTakeawayQueue')) {
        dispatch(operationsApi.util.updateQueryData('kitchenTakeawayQueue', args, patchTickets));
      }
    }
  };
